import * as THREE from 'three';

export function getRandomSpherePoint(radius) {
  const theta = Math.random() * Math.PI * 2;
  const phi = Math.acos(2 * Math.random() - 1);
  const r = radius * (1 + Math.random() * 0.3);
  return new THREE.Vector3(r * Math.sin(phi) * Math.cos(theta), r * Math.sin(phi) * Math.sin(theta), r * Math.cos(phi));
}

export class PrismParticle {
  constructor(particleGroup, texZero, texOne, color) {
    const isZero = Math.random() > 0.5;
    const mat = new THREE.SpriteMaterial({ map: isZero ? texZero : texOne, color: color, transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending });
    this.mesh = new THREE.Sprite(mat);
    this.mesh.scale.set(0.2, 0.2, 0.2);
    this.mesh.renderOrder = 2;
    this.target = getRandomSpherePoint(6.0);
    this.start = new THREE.Vector3(0, 0, 0);
    this.progress = 0;
    this.speed = 1.0 + Math.random();
    this.state = "moving";
    this.spiralPhase = Math.random() * Math.PI * 2;
    particleGroup.add(this.mesh);
  }

  update(delta) {
    if (this.state === "moving") {
      this.progress += delta * this.speed;
      const ease = 1 - Math.pow(1 - Math.min(this.progress, 1), 3);
      const currentPos = new THREE.Vector3().lerpVectors(this.start, this.target, ease);
      const spiralR = (1 - ease) * 2.0;
      currentPos.x += Math.cos(this.progress * 10 + this.spiralPhase) * spiralR;
      currentPos.y += Math.sin(this.progress * 10 + this.spiralPhase) * spiralR;
      this.mesh.position.copy(currentPos);
      if (this.progress >= 1) {
        this.state = "locked";
        this.mesh.position.copy(this.target);
      }
    } else if (this.state === "locked") {
      const t = Date.now() * 0.001;
      this.mesh.position.x = this.target.x + Math.sin(t + this.target.y) * 0.05;
      this.mesh.position.y = this.target.y + Math.cos(t + this.target.x) * 0.05;
    }
  }
}

export class DataPacket {
  constructor(particleGroup, texDot, color) {
    const mat = new THREE.SpriteMaterial({ map: texDot, color: color, transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending });
    this.mesh = new THREE.Sprite(mat);
    this.mesh.scale.set(0.15, 0.15, 0.15);
    this.mesh.renderOrder = 2;
    this.active = false;
    this.mesh.visible = false;
    particleGroup.add(this.mesh);
  }

  spawn(startPos, endPos) {
    this.start = startPos;
    this.end = endPos;
    this.progress = 0;
    this.speed = 2.5 + Math.random();
    this.active = true;
    this.mesh.visible = true;
    this.mesh.position.copy(startPos);
  }

  update(delta) {
    if (!this.active) return;
    this.progress += delta * this.speed;
    if (this.progress >= 1) {
      this.active = false;
      this.mesh.visible = false;
      return;
    }
    this.mesh.position.lerpVectors(this.start, this.end, this.progress);
  }
}

// Yıldız kayması (meteor) — parlak baş + sönen ince iz çizgisi, arada bir
// gökyüzünü çapraz geçer. Kameraya göre doğar (kamera nereye bakarsa görüş
// alanından geçer).
export class ShootingStars {
  constructor(parent, color = '#bfe9ff', camera = null) {
    this.parent = parent;
    this.camera = camera;
    this.color = new THREE.Color(color);
    this.enabled = true;
    this.frequency = 1;
    this.timer = 0;
    this.nextIn = 1.5 + Math.random() * 3;
    this.TRAIL = 18;
    this.pool = [];
    for (let i = 0; i < 4; i++) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(this.TRAIL * 3), 3));
      geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(this.TRAIL * 3), 3));
      const mat = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false });
      const line = new THREE.Line(geo, mat);
      line.visible = false;
      line.frustumCulled = false;
      parent.add(line);
      this.pool.push({ line, geo, active: false, life: 0, maxLife: 1, head: new THREE.Vector3(), vel: new THREE.Vector3(), trail: [] });
    }
  }

  setColor(c) { this.color.set(c); }

  spawn() {
    const s = this.pool.find((p) => !p.active);
    if (!s) return;
    // Uzak arka planda, geniş alana yayılmış; üstten çapraz aşağı kayar.
    s.head.set(
      (Math.random() - 0.5) * 150,      // geniş yatay yayılım
      14 + Math.random() * 42,          // üst kısım
      -(28 + Math.random() * 52)        // uzak arka plan (element'in çok arkasında)
    );
    const speed = 45 + Math.random() * 55;
    s.vel.set((Math.random() - 0.5) * 1.5, -(0.7 + Math.random() * 0.9), (Math.random() - 0.5) * 0.4).normalize().multiplyScalar(speed);
    s.life = 0;
    s.maxLife = 0.9 + Math.random() * 0.9;
    s.active = true;
    s.line.visible = true;
    s.trail = [];
    for (let i = 0; i < this.TRAIL; i++) s.trail.push(s.head.clone());
  }

  update(delta) {
    if (this.enabled) {
      this.timer += delta;
      if (this.timer >= this.nextIn) {
        this.timer = 0;
        this.nextIn = (1.5 + Math.random() * 4) / Math.max(0.1, this.frequency);
        this.spawn();
      }
    }
    for (const s of this.pool) {
      if (!s.active) continue;
      s.life += delta;
      s.head.addScaledVector(s.vel, delta);
      s.trail.unshift(s.head.clone());
      if (s.trail.length > this.TRAIL) s.trail.length = this.TRAIL;
      const t = s.life / s.maxLife;
      const fade = t < 0.12 ? t / 0.12 : (1 - (t - 0.12) / 0.88);
      const baseAlpha = Math.max(0, fade);
      const pos = s.geo.attributes.position.array;
      const col = s.geo.attributes.color.array;
      for (let i = 0; i < this.TRAIL; i++) {
        const p = s.trail[Math.min(i, s.trail.length - 1)];
        pos[i * 3] = p.x; pos[i * 3 + 1] = p.y; pos[i * 3 + 2] = p.z;
        const tail = 1 - i / (this.TRAIL - 1);   // baş parlak, kuyruk söner
        const white = tail * tail;               // baş beyaz-sıcak
        const a = baseAlpha * tail;
        col[i * 3] = (this.color.r * (1 - white) + white) * a;
        col[i * 3 + 1] = (this.color.g * (1 - white) + white) * a;
        col[i * 3 + 2] = (this.color.b * (1 - white) + white) * a;
      }
      s.geo.attributes.position.needsUpdate = true;
      s.geo.attributes.color.needsUpdate = true;
      if (s.life >= s.maxLife) { s.active = false; s.line.visible = false; }
    }
  }
}

// Arka plan yumuşak (blur'lu) ışık yuvarlakları — bokeh/glow lekeleri; süzülür + nabız atar.
const GLOW_VS = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
const GLOW_FS = `
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uSoftness;   // 0 = keskin, 1 = çok blur'lu
  uniform float uTime;
  uniform float uPhase;
  varying vec2 vUv;
  void main(){
    float d = length(vUv - 0.5) * 2.0;          // 0 merkez → 1 kenar
    float k = mix(7.0, 1.6, clamp(uSoftness, 0.0, 1.0));   // yüksek soft = daha geniş/blur
    float glow = exp(-d * d * k);                // gaussian yumuşak yuvarlak
    float pulse = 0.7 + 0.3 * sin(uTime + uPhase);
    gl_FragColor = vec4(uColor, glow * uOpacity * pulse);
  }
`;

const glowRand = (n) => { const x = Math.sin(n * 127.1) * 43758.5453; return x - Math.floor(x); };

export class GlowOrbs {
  constructor(parent, color = '#00ffff') {
    this.group = new THREE.Group();
    this.group.position.set(0, 0, -35);   // arka plan
    this.group.visible = false;
    parent.add(this.group);
    this.geo = new THREE.PlaneGeometry(1, 1);
    this.orbs = [];
    this.count = 0;
    this.time = 0;
    this.color = color;
  }

  rebuild(count, params) {
    for (const o of this.orbs) { this.group.remove(o.mesh); o.mat.dispose(); }
    this.orbs = [];
    for (let i = 0; i < count; i++) {
      const mat = new THREE.ShaderMaterial({
        uniforms: {
          uColor: { value: new THREE.Color(params.glowColor || this.color) },
          uOpacity: { value: params.glowOpacity ?? 0.25 },
          uSoftness: { value: params.glowSoftness ?? 0.6 },
          uTime: { value: 0 },
          uPhase: { value: i * 1.7 }
        },
        vertexShader: GLOW_VS,
        fragmentShader: GLOW_FS,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
        fog: false
      });
      const mesh = new THREE.Mesh(this.geo, mat);
      mesh.userData = {
        bx: (glowRand(i + 1) - 0.5) * 110,
        by: (glowRand(i + 41) - 0.5) * 64,
        dphase: glowRand(i + 7) * 6.28,
        dspeed: 0.5 + glowRand(i + 23) * 0.9,
        sizeMul: 0.6 + glowRand(i + 60) * 0.8
      };
      this.group.add(mesh);
      this.orbs.push({ mesh, mat });
    }
    this.count = count;
  }

  update(delta, params) {
    const enabled = params.glowEnabled === true;
    this.group.visible = enabled;
    if (!enabled) return;
    const count = Math.max(0, Math.floor(params.glowCount ?? 5));
    if (count !== this.count) this.rebuild(count, params);
    this.time += delta;
    const speed = params.glowSpeed ?? 0.3;
    const size = params.glowSize ?? 30;
    for (const o of this.orbs) {
      const u = o.mesh.userData;
      o.mesh.position.x = u.bx + Math.sin(this.time * speed * u.dspeed + u.dphase) * 9;
      o.mesh.position.y = u.by + Math.cos(this.time * speed * u.dspeed * 0.8 + u.dphase) * 7;
      o.mesh.scale.set(size * u.sizeMul, size * u.sizeMul, 1);
      o.mat.uniforms.uTime.value = this.time * speed * 2.5 + u.dphase;
      o.mat.uniforms.uColor.value.set(params.glowColor || this.color);
      o.mat.uniforms.uOpacity.value = params.glowOpacity ?? 0.25;
      o.mat.uniforms.uSoftness.value = params.glowSoftness ?? 0.6;
    }
  }
}
